import { parseISO } from "date-fns";
import React from "react";

import { useI18n } from "@/lib/i18n/i18n_provider";
import { groupBy } from "@/lib/js_utils/array_utils";
import { Day } from "@/offering/day_utils";
import { RecurringExpansion } from "@/offering_v2/utils/expand_recurrence";
import { reasonLabel, shortDate } from "./recurring_booking_copy";
import styles from "./recurring_repeat_pane.module.css";

interface Row {
  date: Day;
  skipped: boolean;
  note?: string;
}

// Every date the pattern touches, in order: the ones that will be booked and,
// struck through with the reason, the ones that were skipped. No prices — the
// picker is availability-only and the quote prices the dates after Save.
export function rowsOf(
  expansion: RecurringExpansion,
  labelReason: (reason: Row["note"]) => string
): Row[] {
  const rows: Row[] = [
    ...expansion.booked.map((date) => ({ date, skipped: false })),
    ...expansion.skipped.map((s) => ({
      date: s.date,
      skipped: true,
      note: labelReason(s.reason),
    })),
  ];
  return rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

interface RecurringDatesListProps {
  expansion: RecurringExpansion;
  // Group rows under a month header, for the long list on the review screen.
  groupByMonth?: boolean;
}

export function RecurringDatesList({
  expansion,
  groupByMonth,
}: RecurringDatesListProps) {
  const { fbt } = useI18n();
  const rows = rowsOf(expansion, (reason) => reasonLabel(reason as any, fbt));

  const renderRow = (row: Row) => (
    <div
      key={row.date}
      className={styles.row}
      data-testid={row.skipped ? "recurring-date-skipped" : "recurring-date"}
    >
      <span className={row.skipped ? styles.rowSkipped : undefined}>
        {shortDate(row.date, fbt)}
      </span>
      <span className={styles.rowNote}>
        {row.skipped ? `${fbt("Unavailable")} · ${row.note}` : ""}
      </span>
    </div>
  );

  if (!groupByMonth) {
    return <div>{rows.map(renderRow)}</div>;
  }

  const groups = groupBy(rows, (row) =>
    fbt.formatDate(parseISO(row.date), { month: "long", year: "numeric" })
  );

  return (
    <div>
      {Object.entries(groups).map(([label, groupRows]) => (
        <div key={label}>
          <div className={styles.monthHeader}>{label}</div>
          {groupRows.map(renderRow)}
        </div>
      ))}
    </div>
  );
}
